import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { VelocityComponent } from 'velocity-react';

import ToolTip from './ToolTip';
import Button from '../button/Button';
import Icon from '../icon/Icon';
import RenderInBody from '../RenderInBody';
import Alert from './Alert';

import ModalDisabledOverlay from './ModalDisabledOverlay';

import { clearModal, closeModal } from './actions';
import { modalProps, disabled, visible } from './reducer';

const modalStyles = modalProps =>
  classnames('modal mrl5-sm', {
    'pt12-sm pb5-sm prl4-sm prl8-md pb12-md prl12-lg': !modalProps.noPadding,
    modal__tooltip: modalProps.type === 'tooltip',
    restrictedHeight: modalProps.restrictedHeight,
    fullScreen: modalProps.fullScreen,
    [`${modalProps.extraClassName}`]: modalProps.extraClassName && modalProps.extraClassName !== ''
  });

const animationDuration = 300;

const overlayAnimationProps = visible => ({
  animation: { opacity: visible ? 1 : 0 },
  visibility: visible ? 'visible' : 'hidden',
  duration: animationDuration
});

const modalAnimationProps = (visible, onClose) => {
  const delay = visible ? 100 : 0;
  const duration = animationDuration - delay;

  return {
    animation: { translateY: visible ? 0 : '+=15px' },
    delay,
    duration,
    complete: () => {
      if (!visible) onClose();
    }
  };
};

export class Modal extends Component {
  constructor(props) {
    super(props);

    this.onKeyDown = this.onKeyDown.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.onKeyDown);
  }

  componentDidUpdate() {
    const { visible } = this.props;
    if (visible) {
      document.body.classList.add('modal__open');
    } else {
      document.body.classList.remove('modal__open');
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
  }

  onKeyDown(event) {
    const { visible, closeModal } = this.props;
    if (visible && event.keyCode === 27) closeModal();
  }

  handleOverlayClick(event) {
    const { modalProps, closeModal } = this.props;
    const canCancel = modalProps.canCancel !== false;

    if (canCancel && event.target === event.currentTarget) closeModal();
  }

  render() {
    const { closeModal, clearModal, modalProps, disabled, visible } = this.props;

    return (
      <RenderInBody>
        <VelocityComponent {...overlayAnimationProps(visible)}>
          <div className='modal-overlay' data-testid='modal' onClick={this.handleOverlayClick} aria-hidden={!visible}>
            <VelocityComponent {...modalAnimationProps(visible, clearModal)}>
              <div className={modalStyles(modalProps)}>
                <VelocityComponent animation={disabled && modalProps.disabledContent ? 'fadeIn' : 'fadeOut'}>
                  <ModalDisabledOverlay>{modalProps.disabledContent}</ModalDisabledOverlay>
                </VelocityComponent>
                {modalProps.type === 'alert' && <Alert {...modalProps} disabled={disabled} />}
                {modalProps.type === 'tooltip' && <ToolTip {...modalProps} />}
                {modalProps.type === 'custom' && (
                  <>
                    {modalProps.element}
                    <Button
                      className={classnames('prl2-sm modal-close', {
                        'modal-close-fixed': modalProps.fullScreen
                      })}
                      onClick={modalProps.cancelAction || closeModal}
                      aria-label='Close Modal'
                      data-desc='alert__close_x'
                    >
                      <Icon name='x' alt='close icon' />
                    </Button>
                  </>
                )}
              </div>
            </VelocityComponent>
          </div>
        </VelocityComponent>
      </RenderInBody>
    );
  }
}

Modal.defaultProps = {
  modalProps: {
    type: 'alert',
    noPadding: false
  },
  disabled: false,
  visible: false
};

Modal.propTypes = {
  clearModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  modalProps: PropTypes.object,
  disabled: PropTypes.bool,
  visible: PropTypes.bool
};

const mapStateToProps = state => ({
  modalProps: modalProps(state) || Modal.defaultProps.modalProps,
  disabled: disabled(state),
  visible: visible(state)
});

export default connect(mapStateToProps, {
  clearModal,
  closeModal
})(Modal);
